import { setCurrectChat } from "../../../../redux/user-slices/socket.slice";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemText,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Typography,
  Badge,
  Grid2 as Grid
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Icons from "../../../../assets/js/Icons";
import moment from "moment";
import { showDialogWithData } from "../../../../redux/guest-slices/other.slice";
import { useEffect, useMemo, useState } from "react";
import { APPOINTMENT_COMPLETE_POPUP } from "utils/keywords";
import { checkUserType } from "../../../../utils/helper";

function Header({ availability, isAppointmentChat = false }) {
  const dispatch = useDispatch();

  const [sending, setSending] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isUserOnline, setIsUserOnline] = useState(false);

  const { socket, chat, appointmentAvailability } = useSelector(
    (state) => state.socketSlice
  );
  const { member, specialist } = useSelector((state) => state.authSlice);
  const currentUserId = member?._id || specialist?._id || specialist?.specialist?._id;

  const targetUserId = useMemo(() => {
    const otherParticipant = chat?.participants?.find(
      (p) => p && (p._id || p).toString() !== currentUserId?.toString()
    );
    return (otherParticipant?._id || otherParticipant || chat?.user?._id)?.toString();
  }, [chat, currentUserId]);

  const userRole = useSelector(
    (state) => state?.specialistSlice?.specialist?.specialist?.userRole
  );

  useEffect(() => {
    if (!socket || !targetUserId) return;

    socket.emit("check-online-status", targetUserId, (res) => {
      if (res?.userId === targetUserId) {
        setIsUserOnline(Boolean(res.isOnline));
      }
    });

    const handleStatusChange = (data) => {
      if (data?.userId === targetUserId) {
        setIsUserOnline(Boolean(data.isOnline));
      }
    };

    socket.on("user-status-changed", handleStatusChange);
    socket.on("online-status-response", handleStatusChange);

    return () => {
      socket.off("user-status-changed", handleStatusChange);
      socket.off("online-status-response", handleStatusChange);
    };
  }, [socket, targetUserId]);

  const currentAvailability = useMemo(() => {
    return availability || chat?.appointment || appointmentAvailability;
  }, [availability, chat, appointmentAvailability]);

  const availabilityStatus = useMemo(() => {
    if (
      !currentAvailability?.startTime ||
      !currentAvailability?.endTime ||
      !(currentAvailability?.timeZone || currentAvailability?.timezone)
    ) {
      return "invalid";
    }

    const tz = currentAvailability.timeZone || currentAvailability.timezone;

    const start = moment(currentAvailability.startTime).tz(tz);
    const end = moment(currentAvailability.endTime).tz(tz);
    const now = moment().tz(tz);

    if (now.isBefore(start)) {
      return "before";
    }

    if (now.isAfter(end)) {
      return "after";
    }

    return "during";
  }, [currentAvailability]);

  useEffect(() => {
    if (!socket) return;

    const handleAppointmentCompletePopup = (data) => {
      dispatch(
        showDialogWithData({
          type: APPOINTMENT_COMPLETE_POPUP,
          data,
        })
      );
    };

    socket.on(
      "appointment-complete-popup",
      handleAppointmentCompletePopup
    );

    return () => {
      socket.off(
        "appointment-complete-popup",
        handleAppointmentCompletePopup
      );
    };
  }, [socket, userRole]);

  const onMarkAsComplete = async () => {
    if (!socket || !chat?._id) return;

    setSending(true);

    socket.emit("appointment-complete-request", {
      roomId: chat?._id,
      sender: userRole,
      message: "Specialist marked appointment as complete",
    });

    setSuccessOpen(true);

    setTimeout(() => {
      setSending(false);
    }, 1000);
  };



  const currentChatType = isAppointmentChat || chat?.type === "appointment" || chat?.conversation_type === "appointment";

  const chipVariants = {
    rejected: "error",
    confirmed: "success",
    completed: "success",
    cancelled: "default",
    late_cancelled: "error",
    no_refund_cancelled: "error",
    expired: "default",
    pending: "warning",
    scheduled: "info",
    no_show_specialist: "warning",
    no_show_member: "warning",
    "no-show": "warning"
  };

  const bookingRef = useMemo(() => {
    const id = chat?.appointmentId || chat?.appointment?._id || chat?._id;
    return id ? id.toString().slice(-6).toUpperCase() : "N/A";
  }, [chat]);

  const memberName = useMemo(() => {
    return chat?.appointment?.member?.fullName ||
      (chat?.participants?.find(p => p.userRole === "member")?.fullName) ||
      (userRole === "member" ? "Me" : chat?.user?.fullName) || "Member";
  }, [chat, userRole]);

  const specialistName = useMemo(() => {
    return chat?.appointment?.specialist?.fullName ||
      (chat?.participants?.find(p => p.userRole === "specialist")?.fullName) ||
      (userRole === "specialist" ? "Me" : chat?.user?.fullName) || "Specialist";
  }, [chat, userRole]);

  const appointmentStatus = useMemo(() => {
    return chat?.appointmentStatus || chat?.appointment?.appointmentStatus || "pending";
  }, [chat]);

  const appointmentTime = useMemo(() => {
    const start = chat?.appointment?.startTime || appointmentAvailability?.startTime;
    const end = chat?.appointment?.endTime || appointmentAvailability?.endTime;
    const tz = chat?.appointment?.timezone || appointmentAvailability?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!start) return "N/A";

    return `${moment(start).tz(tz).format("MM-DD-YYYY")} ${moment(start).tz(tz).format("hh:mm A")} - ${moment(end).tz(tz).format("hh:mm A")} (${tz})`;
  }, [chat, appointmentAvailability]);

  return (
    <>
      {currentChatType ? (
        <Box
          sx={{
            p: 1.5,
            borderBottom: "1px solid #e0e0e0",
            bgcolor: "#f4f6f9",
            display: "flex",
            flexDirection: "column",
            gap: 1
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" fontWeight="700" color="primary.main">
              APPOINTMENT CHAT
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {checkUserType() === "specialist" &&
                !["completed", "cancelled", "expired", "late_cancelled", "no_refund_cancelled", "no_show_specialist", "no_show_member"].includes(
                  appointmentStatus
                ) &&
                availabilityStatus === "during" && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    disabled={sending}
                    onClick={onMarkAsComplete}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: "unset",
                      px: 1.8,
                      py: 0.6,
                      fontSize: "12px",
                      lineHeight: 1.3,
                      height: 28,
                    }}
                  >
                    {sending ? "Sending..." : "Mark As Complete"}
                  </Button>
                )}
              <Chip
                size="small"
                label={appointmentStatus?.toUpperCase()}
                color={chipVariants[appointmentStatus] || "primary"}
              />
              <IconButton
                size="small"
                onClick={() => {
                  dispatch(setCurrectChat({}));
                }}
              >
                <Icons.Cross />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                <b>Booking Ref:</b> {bookingRef}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                <b>Member:</b> {memberName}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                <b>Specialist:</b> {specialistName}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                <b>Session:</b> {appointmentTime}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            p: 1,
            gap: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            variant="dot"
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: isUserOnline ? "#44b700" : "#a0aec0",
                color: isUserOnline ? "#44b700" : "#a0aec0",
                boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                "&::after": isUserOnline
                  ? {
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      animation: "ripple 1.2s infinite ease-in-out",
                      border: "1px solid currentColor",
                      content: '""',
                    }
                  : {},
              },
              "@keyframes ripple": {
                "0%": { transform: "scale(.8)", opacity: 1 },
                "100%": { transform: "scale(2.4)", opacity: 0 },
              },
            }}
          >
            <Avatar
              src={chat?.user?.image}
              sx={{ width: 40, height: 40 }}
            />
          </Badge>

          <ListItemText
            sx={{ m: 0 }}
            primary={chat?.user?.fullName || "Unknown user"}
            secondary={
              <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: isUserOnline ? "success.main" : "#a0aec0",
                    display: "inline-block",
                    boxShadow: isUserOnline ? "0 0 6px rgba(46, 125, 50, 0.6)" : "none",
                  }}
                />
                <Typography variant="caption" sx={{ color: isUserOnline ? "success.main" : "text.secondary", fontWeight: 600, fontSize: 12 }}>
                  {isUserOnline ? "Online" : "Offline"}
                </Typography>
              </Box>
            }
            slotProps={{
              primary: {
                sx: {
                  fontWeight: "500",
                  lineHeight: 1.2,
                  textTransform: "capitalize",
                },
              },
            }}
          />

          {userRole === "specialist" &&
            !["completed", "no_show_specialist", "no_show_member"].includes(
              chat?.appointmentStatus
            ) &&
            availabilityStatus === "during" && (
              <Button
                variant="contained"
                size="small"
                color="success"
                disabled={sending}
                onClick={onMarkAsComplete}
                sx={{
                  borderRadius: "5px",
                  textTransform: "none",
                  fontWeight: 600,
                  ml: 1,
                  minWidth: "unset",
                  px: 1.8,
                  py: 0.6,
                  fontSize: "12px",
                  lineHeight: 1.3,
                  height: 32,
                }}
              >
                {sending ? "Sending..." : "Mark As Complete"}
              </Button>
            )}
          <IconButton
            size="small"
            sx={{ ml: 1 }}
            onClick={() => {
              dispatch(setCurrectChat({}));
            }}
          >
            <Icons.Cross />
          </IconButton>
        </Box>
      )}

      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessOpen(false)}
        >
          Completion request sent successfully
        </Alert>
      </Snackbar>
    </>
  );
}

export default Header;